import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { X, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';

import { cn } from '@/lib/utils/index';

// ============================================
// VARIANTS
// ============================================
const toastVariants = cva(
  'relative flex items-start gap-3 w-full max-w-sm rounded-lg border p-4 shadow-lg transition-all duration-300 ease-in-out',
  {
    variants: {
      variant: {
        success:
          'bg-success-50 text-success-700 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
        warning:
          'bg-warning-50 text-warning-700 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
        error:
          'bg-error-50 text-error-700 border-error-200 dark:bg-error-900/20 dark:text-error-300 dark:border-error-800',
        info: 'bg-primary-50 text-primary-700 border-primary-200 dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-800',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

// ============================================
// TYPES
// ============================================
export type ToastVariant = 'success' | 'warning' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps extends VariantProps<typeof toastVariants> {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
  action?: ToastAction;
  onClose: () => void;
}

// ============================================
// ICONS
// ============================================
const VARIANT_ICONS: Record<ToastVariant, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

const ICON_COLORS: Record<ToastVariant, string> = {
  success: 'text-success-500 dark:text-success-400',
  warning: 'text-warning-500 dark:text-warning-400',
  error: 'text-error-500 dark:text-error-400',
  info: 'text-primary-500 dark:text-primary-400',
};

// ============================================
// COMPONENT
// ============================================
const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ id, title, description, variant = 'info', action, onClose, ...props }, ref) => {
    const Icon = VARIANT_ICONS[variant];

    return (
      <div
        ref={ref}
        role="alert"
        aria-live="polite"
        data-toast-id={id}
        className={cn(toastVariants({ variant }))}
        {...props}
      >
        {/* Icon */}
        <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', ICON_COLORS[variant])} />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">{title}</p>
          {description && <p className="mt-1 text-sm opacity-90 leading-relaxed">{description}</p>}

          {/* Action button */}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className={cn(
                'mt-2 text-sm font-medium underline underline-offset-2',
                'hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                'focus-visible:ring-current'
              )}
            >
              {action.label}
            </button>
          )}
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer la notification"
          className={cn(
            'flex-shrink-0 rounded-md p-1 -m-1',
            'hover:bg-black/10 dark:hover:bg-white/10',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
            'focus-visible:ring-current',
            'transition-colors duration-150'
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
);

Toast.displayName = 'Toast';

export { Toast, toastVariants };
